import { atom, computed } from 'nanostores';
import { z } from 'zod';

export const SUBTITLE_LANGUAGES = ['auto', 'zh-CN', 'zh-TW', 'ja', 'en'] as const;

const PLAYER_SETTINGS_KEY = 'bgmer.playerSettings';
const playerSettingsSchema = z
  .object({
    autoplay: z.boolean().default(true),
    autoPlayNextEpisode: z.boolean().default(true),
    autoMarkWatched: z.boolean().default(false),
    playbackRate: z
      .union([z.literal(0.5), z.literal(0.75), z.literal(1), z.literal(1.25), z.literal(1.5), z.literal(2)])
      .default(1),
    subtitlesEnabled: z.boolean().default(true),
    subtitleLanguage: z.enum(SUBTITLE_LANGUAGES).default('auto'),
    subtitleDelay: z.number().min(-60).max(60).default(0),
    danmakuVisible: z.boolean().default(true),
    danmakuFontSize: z.union([z.literal(18), z.literal(24), z.literal(32), z.literal(42)]).default(24),
    danmakuSpeed: z.union([z.literal(84), z.literal(144), z.literal(192), z.literal(240)]).default(144),
    danmakuOutlineEnabled: z.boolean().default(true),
    danmakuShadowEnabled: z.boolean().default(false),
  })
  .strict();

export type PlayerSettings = z.infer<typeof playerSettingsSchema>;
export type SubtitleLanguage = (typeof SUBTITLE_LANGUAGES)[number];

const defaultPlayerSettings = (): PlayerSettings => playerSettingsSchema.parse({});

const loadPlayerSettings = (): PlayerSettings => {
  try {
    const saved = localStorage.getItem(PLAYER_SETTINGS_KEY);
    return saved ? playerSettingsSchema.parse(JSON.parse(saved)) : defaultPlayerSettings();
  } catch {
    return defaultPlayerSettings();
  }
};

export const $playerSettings = atom<PlayerSettings>(loadPlayerSettings());
export const $autoplay = computed($playerSettings, (settings) => settings.autoplay);
export const $autoPlayNextEpisode = computed($playerSettings, (settings) => settings.autoPlayNextEpisode);
export const $autoMarkWatched = computed($playerSettings, (settings) => settings.autoMarkWatched);
export const $playbackRate = computed($playerSettings, (settings) => settings.playbackRate);
export const $subtitlesEnabled = computed($playerSettings, (settings) => settings.subtitlesEnabled);
export const $subtitleLanguage = computed($playerSettings, (settings) => settings.subtitleLanguage);
export const $subtitleDelay = computed($playerSettings, (settings) => settings.subtitleDelay);
export const $danmakuVisible = computed($playerSettings, (settings) => settings.danmakuVisible);
export const $danmakuFontSize = computed($playerSettings, (settings) => settings.danmakuFontSize);
export const $danmakuSpeed = computed($playerSettings, (settings) => settings.danmakuSpeed);
export const $danmakuOutlineEnabled = computed($playerSettings, (settings) => settings.danmakuOutlineEnabled);
export const $danmakuShadowEnabled = computed($playerSettings, (settings) => settings.danmakuShadowEnabled);

const updatePlayerSetting = <Key extends keyof PlayerSettings>(key: Key, value: PlayerSettings[Key]): void => {
  const settings = playerSettingsSchema.parse({ ...$playerSettings.get(), [key]: value });
  $playerSettings.set(settings);
  localStorage.setItem(PLAYER_SETTINGS_KEY, JSON.stringify(settings));
};

export const setAutoplay = (value: boolean): void => updatePlayerSetting('autoplay', value);
export const setAutoPlayNextEpisode = (value: boolean): void => updatePlayerSetting('autoPlayNextEpisode', value);
export const setAutoMarkWatched = (value: boolean): void => updatePlayerSetting('autoMarkWatched', value);
export const setPlaybackRate = (value: PlayerSettings['playbackRate']): void =>
  updatePlayerSetting('playbackRate', value);
export const setSubtitlesEnabled = (value: boolean): void => updatePlayerSetting('subtitlesEnabled', value);
export const setSubtitleLanguage = (value: SubtitleLanguage): void => updatePlayerSetting('subtitleLanguage', value);
export const setSubtitleDelay = (value: number): void => updatePlayerSetting('subtitleDelay', value);
export const setDanmakuVisible = (value: boolean): void => updatePlayerSetting('danmakuVisible', value);
export const setDanmakuFontSize = (value: PlayerSettings['danmakuFontSize']): void =>
  updatePlayerSetting('danmakuFontSize', value);
export const setDanmakuSpeed = (value: PlayerSettings['danmakuSpeed']): void =>
  updatePlayerSetting('danmakuSpeed', value);
export const setDanmakuOutlineEnabled = (value: boolean): void => updatePlayerSetting('danmakuOutlineEnabled', value);
export const setDanmakuShadowEnabled = (value: boolean): void => updatePlayerSetting('danmakuShadowEnabled', value);
